import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MentionUser } from "../utils/messageDisplay.ts";
import {
  ACCEPT_ATTACHMENTS,
  MAX_REPLY_ATTACHMENTS,
  attachmentPreviewKind,
  filesFromClipboard,
  filesFromDataTransfer,
  isAttachableFile,
} from "../utils/attachments.ts";
import { apiWithRetry } from "../api.ts";
import type { MailPreview } from "../types.ts";
import { readUiState, writeUiState } from "../utils/uiState.ts";

type PendingFile = { id: string; file: File; previewUrl: string | null };

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function normalizeEmailList(emails: string[] | undefined, fallback: string | null | undefined): string[] {
  const raw = emails?.length ? emails : (fallback ? [fallback] : []);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const e of raw) {
    const k = (e || "").trim().toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
}

export function ReplyComposer({
  ticketId,
  reply,
  replyToCustomer,
  contactEmail,
  contactName,
  contactEmails,
  isFirstCustomerReply,
  onEditContacts,
  mentionUsers,
  aiBusy,
  aiMenuOpen,
  aiMenuRef,
  aiSummary,
  sending,
  draftSavedAt,
  onReplyChange,
  onReplyToCustomerChange,
  onAiMenuToggle,
  onAiDraft,
  onAiSummarize,
  onAiFaqSuggest,
  faqSuggestEnabled,
  onKbOpen,
  onClearAiSummary,
  onSend,
}: {
  ticketId: number;
  reply: string;
  replyToCustomer: boolean;
  contactEmail: string | null | undefined;
  /** 宛先バーの表示用（改善 F-2） */
  contactName?: string | null;
  contactEmails?: string[];
  /** このチケットで顧客へ送るのが初回か（改善 F-3 の確認ダイアログ条件） */
  isFirstCustomerReply?: boolean;
  onEditContacts?: () => void;
  mentionUsers: MentionUser[];
  aiBusy: null | "draft" | "summary" | "faq";
  aiMenuOpen: boolean;
  aiMenuRef: React.RefObject<HTMLDivElement | null>;
  aiSummary: string;
  sending: boolean;
  /** 親が localStorage へ本文を自動保存した時刻（表示用） */
  draftSavedAt?: number | null;
  onReplyChange: (v: string) => void;
  onReplyToCustomerChange: (v: boolean) => void;
  onAiMenuToggle: () => void;
  onAiDraft: () => void;
  onAiSummarize: () => void;
  onAiFaqSuggest: () => void;
  faqSuggestEnabled: boolean;
  onKbOpen: () => void;
  onClearAiSummary: () => void;
  onSend: (attachmentFiles: File[], toEmails?: string[]) => void | Promise<void>;
}) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const expandTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragDepthRef = useRef(0);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionFilter, setMentionFilter] = useState("");
  const [mentionPick, setMentionPick] = useState(0);
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // 既定は 3 行。フォーカス中だけ 8 行相当へ広げる（改善 B-5）
  const [inputFocused, setInputFocused] = useState(false);
  const ticketEmailsKey = (contactEmails || []).join("|") + "|" + (contactEmail || "");
  const [toCandidates, setToCandidates] = useState<string[]>(() => {
    const base = normalizeEmailList(contactEmails, contactEmail);
    const saved = readUiState<string[]>(`reply.to.${ticketId}`, []);
    const seen = new Set(base);
    const merged = [...base];
    for (const e of saved) {
      const k = (e || "").toLowerCase();
      if (k && !seen.has(k)) {
        seen.add(k);
        merged.push(k);
      }
    }
    return merged;
  });
  const [selectedTo, setSelectedTo] = useState<Set<string>>(() => {
    const saved = readUiState<string[]>(`reply.to.${ticketId}`, []);
    if (saved.length) return new Set(saved.map((e) => e.toLowerCase()));
    return new Set(normalizeEmailList(contactEmails, contactEmail));
  });
  const [addToInput, setAddToInput] = useState("");
  const [addToErr, setAddToErr] = useState("");
  const [mailPreview, setMailPreview] = useState<MailPreview | null>(null);
  const [mailPreviewErr, setMailPreviewErr] = useState("");

  // チケット連絡先が変わったら候補を同期（手動追加分は維持）
  useEffect(() => {
    const next = normalizeEmailList(contactEmails, contactEmail);
    setToCandidates((prev) => {
      const seen = new Set(next);
      const extra = prev.filter((e) => !seen.has(e));
      return [...next, ...extra];
    });
    const saved = readUiState<string[]>(`reply.to.${ticketId}`, []);
    if (saved.length) {
      setSelectedTo(new Set(saved.map((e) => e.toLowerCase())));
    } else {
      setSelectedTo(new Set(next));
    }
    setAddToInput("");
    setAddToErr("");
  }, [ticketId, ticketEmailsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedToList = useMemo(() => Array.from(selectedTo), [selectedTo]);
  const toSummary = selectedToList.length ? selectedToList.join(", ") : "（未選択）";

  // 送信先選択を随時自動保存
  useEffect(() => {
    writeUiState(`reply.to.${ticketId}`, selectedToList);
  }, [ticketId, selectedToList]);

  // 拡大編集中かつ顧客返信: 実際に届くメールを随時プレビュー
  useEffect(() => {
    if (!expanded || !replyToCustomer) {
      setMailPreview(null);
      setMailPreviewErr("");
      return;
    }
    let alive = true;
    const timer = window.setTimeout(() => {
      apiWithRetry<MailPreview>(`/support/tickets/${ticketId}/mail-preview`, {
        method: "POST",
        body: JSON.stringify({
          kind: "reply",
          body: reply,
          to_addrs: selectedToList,
        }),
      })
        .then((d) => {
          if (!alive) return;
          setMailPreview(d);
          setMailPreviewErr("");
        })
        .catch((e) => {
          if (!alive) return;
          setMailPreviewErr(e instanceof Error ? e.message : String(e));
        });
    }, 400);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [expanded, replyToCustomer, ticketId, reply, selectedToList]);

  const canAttach = !sending && pending.length < MAX_REPLY_ATTACHMENTS;

  const canSend = !sending
    && (reply.trim().length > 0 || pending.length > 0)
    && (!replyToCustomer || selectedToList.length > 0);

  const filteredMentions = useMemo(() => {
    if (!mentionOpen || replyToCustomer) return [];
    const q = mentionFilter.toLowerCase();
    return mentionUsers
      .filter((u) => {
        if (!q) return true;
        const label = u.label.toLowerCase();
        const email = u.email.toLowerCase();
        return label.includes(q) || email.includes(q) || email.split("@")[0].includes(q);
      })
      .slice(0, 10);
  }, [mentionFilter, mentionOpen, mentionUsers, replyToCustomer]);

  const showMentionPanel = mentionOpen && !replyToCustomer;

  useEffect(() => {
    if (!expanded) return;
    const t = window.setTimeout(() => {
      const ta = expandTextareaRef.current;
      if (!ta) return;
      ta.focus();
      const len = ta.value.length;
      ta.setSelectionRange(len, len);
    }, 0);
    return () => window.clearTimeout(t);
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !showMentionPanel) {
        e.preventDefault();
        setExpanded(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded, showMentionPanel]);

  const toggleToEmail = (email: string) => {
    setSelectedTo((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const addToEmail = () => {
    const raw = addToInput.trim().toLowerCase();
    setAddToErr("");
    if (!raw || !EMAIL_RE.test(raw)) {
      setAddToErr("メールアドレスの形式を確認してください");
      return;
    }
    setToCandidates((prev) => (prev.includes(raw) ? prev : [...prev, raw]));
    setSelectedTo((prev) => new Set(prev).add(raw));
    setAddToInput("");
  };

  const renderRecipientPicker = (compact: boolean) => {
    if (!replyToCustomer) return null;
    return (
      <div className={"support-composer-to" + (compact ? "" : " support-composer-to--expand")}>
        <div className="support-composer-to__head">
          <span className="support-composer-to__label">送信先</span>
          {contactName ? <span className="support-composer-to__name">{contactName}</span> : null}
          {onEditContacts && (
            <button type="button" className="support-composer-to__edit" onClick={onEditContacts}>連絡先を編集</button>
          )}
        </div>
        {toCandidates.length === 0 ? (
          <div className="support-composer-to__empty">宛先がありません。下で追加してください。</div>
        ) : (
          <div className="support-composer-to__list">
            {toCandidates.map((email) => (
              <label key={email} className="support-composer-to__item">
                <input
                  type="checkbox"
                  checked={selectedTo.has(email)}
                  onChange={() => toggleToEmail(email)}
                />
                <span className="mono">{email}</span>
              </label>
            ))}
          </div>
        )}
        <div className="support-composer-to__add">
          <input
            type="email"
            value={addToInput}
            onChange={(e) => setAddToInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addToEmail(); } }}
            placeholder="宛先を追加"
          />
          <button type="button" onClick={addToEmail} disabled={!addToInput.trim()}>追加</button>
        </div>
        {addToErr && <div className="support-composer-to__err">{addToErr}</div>}
        <div className="support-composer-to__summary">
          今回の送信先: <span className="mono">{toSummary}</span>
        </div>
      </div>
    );
  };

  const syncMentionState = (value: string, cursor: number) => {
    if (replyToCustomer) {
      setMentionOpen(false);
      return;
    }
    const before = value.slice(0, cursor);
    const m = before.match(/@([\w.-]*)$/);
    if (m) {
      setMentionOpen(true);
      setMentionFilter(m[1] || "");
      setMentionPick(0);
    } else {
      setMentionOpen(false);
    }
  };

  const insertMention = (user: MentionUser) => {
    const ta = (expanded ? expandTextareaRef.current : textareaRef.current);
    const cursor = ta?.selectionStart ?? reply.length;
    const before = reply.slice(0, cursor);
    const after = reply.slice(cursor);
    const at = before.match(/@([\w.-]*)$/);
    if (!at) return;
    const prefix = before.slice(0, before.length - at[0].length);
    const token = `@${user.label}`;
    const next = `${prefix}${token} ${after}`;
    onReplyChange(next);
    setMentionOpen(false);
    requestAnimationFrame(() => {
      const pos = prefix.length + token.length + 1;
      ta?.focus();
      ta?.setSelectionRange(pos, pos);
    });
  };

  const addPendingFiles = useCallback((files: File[]) => {
    const allowed = files.filter(isAttachableFile);
    if (!allowed.length) return;
    setPending((prev) => {
      const room = MAX_REPLY_ATTACHMENTS - prev.length;
      if (room <= 0) return prev;
      const next = [...prev];
      for (const file of allowed.slice(0, room)) {
        const previewUrl = attachmentPreviewKind(file) === "image" ? URL.createObjectURL(file) : null;
        next.push({ id: `${Date.now()}-${Math.random()}`, file, previewUrl });
      }
      return next;
    });
  }, []);

  const removePending = useCallback((id: string) => {
    setPending((prev) => {
      const item = prev.find((p) => p.id === id);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  }, []);

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = filesFromClipboard(e.clipboardData);
    if (!files.length) return;
    e.preventDefault();
    addPendingFiles(files);
  };

  const dragHasFiles = (e: React.DragEvent) => Array.from(e.dataTransfer.types).includes("Files");

  const onDragEnter = (e: React.DragEvent) => {
    if (!canAttach || !dragHasFiles(e)) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setDragOver(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    if (!dragHasFiles(e)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragOver(false);
  };

  const onDragOver = (e: React.DragEvent) => {
    if (!canAttach || !dragHasFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const onDrop = (e: React.DragEvent) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    dragDepthRef.current = 0;
    setDragOver(false);
    if (!canAttach) return;
    addPendingFiles(filesFromDataTransfer(e.dataTransfer));
  };

  const composerDragProps = {
    onDragEnter,
    onDragLeave,
    onDragOver,
    onDrop,
  };

  const handleSend = async () => {
    if (!canSend) return;
    // 毎回の確認は形骸化するため、長文または「このチケットで初めての顧客送信」だけ確認する
    if (replyToCustomer && (reply.trim().length > 200 || isFirstCustomerReply)) {
      const ok = window.confirm(`次の宛先へ送信します。\n\n${selectedToList.join("\n") || "（未選択）"}\n\n送信しますか？`);
      if (!ok) return;
    }
    const files = pending.map((p) => p.file);
    await onSend(files, replyToCustomer ? selectedToList : undefined);
    pending.forEach((p) => { if (p.previewUrl) URL.revokeObjectURL(p.previewUrl); });
    setPending([]);
    setExpanded(false);
  };

  const renderMentionSuggest = () => (
    showMentionPanel ? (
      <div className="support-mention-suggest" role="listbox" aria-label="メンション候補">
        {filteredMentions.length === 0 ? (
          <div className="support-mention-suggest__empty">
            {mentionUsers.length === 0 ? "担当者一覧を読み込めませんでした" : "該当する担当者がいません"}
          </div>
        ) : (
          filteredMentions.map((u, i) => (
            <button
              key={u.email}
              type="button"
              role="option"
              aria-selected={i === mentionPick}
              className={"support-mention-suggest__item" + (i === mentionPick ? " support-mention-suggest__item--active" : "")}
              onMouseEnter={() => setMentionPick(i)}
              onMouseDown={(e) => { e.preventDefault(); insertMention(u); }}
            >
              <span className="support-mention-suggest__label">@{u.label}</span>
              <span className="support-mention-suggest__email">{u.email}</span>
            </button>
          ))
        )}
      </div>
    ) : null
  );

  const onTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showMentionPanel && filteredMentions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionPick((p) => (p + 1) % filteredMentions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionPick((p) => (p - 1 + filteredMentions.length) % filteredMentions.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(filteredMentions[mentionPick]);
        return;
      }
      if (e.key === "Escape") {
        setMentionOpen(false);
        return;
      }
    }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (canSend) void handleSend();
    }
  };

  const renderModeTabs = () => (
    <>
      {([{ k: false, label: "社内メモ" }, { k: true, label: "顧客へ返信" }] as const).map((opt) => {
        const active = replyToCustomer === opt.k;
        // 宛先未登録でも送信プレビューで任意アドレスを追加できるため、タブ自体は常に有効
        const cls = "support-reply-tab"
          + (active ? (opt.k ? " support-reply-tab--active-customer" : " support-reply-tab--active-internal") : "");
        return (
          <button
            key={String(opt.k)}
            type="button"
            className={cls}
            onClick={() => onReplyToCustomerChange(opt.k)}
            title={opt.k && !contactEmail && !(contactEmails && contactEmails.length)
              ? "宛先は送信確認画面で追加できます"
              : ""}
          >
            {opt.k ? "✉️ " : "🔒 "}{opt.label}
          </button>
        );
      })}

    </>
  );

  const renderPending = () => (
    pending.length > 0 ? (
      <div className="support-composer-attachments">
        {pending.map((p) => (
          <div key={p.id} className="support-composer-attachments__item">
            {p.previewUrl ? (
              <img src={p.previewUrl} alt="" className="support-composer-attachments__thumb" />
            ) : (
              <div className="support-composer-attachments__vid">
                {attachmentPreviewKind(p.file) === "video" ? "🎬" : "📄"}
              </div>
            )}
            <span className="support-composer-attachments__name">{p.file.name}</span>
            <button type="button" className="support-composer-attachments__remove" onClick={() => removePending(p.id)} aria-label="削除">×</button>
          </div>
        ))}
      </div>
    ) : null
  );

  const renderSendBtn = () => (
    <button
      type="button"
      className={"support-send-btn " + (replyToCustomer ? "support-send-btn--customer" : "support-send-btn--internal")}
      onClick={() => void handleSend()}
      disabled={!canSend}
    >
      {sending
        ? "送信中…"
        : replyToCustomer
          ? (selectedToList.length === 1
            ? `送信 → ${selectedToList[0]}`
            : selectedToList.length > 1
              ? `送信 → ${selectedToList.length}件`
              : "送信先を選択")
          : "社内メモを保存"}
    </button>
  );

  return (
    <div
      className={"support-ticket-detail__composer"
        + (replyToCustomer ? " support-ticket-detail__composer--customer" : " support-ticket-detail__composer--internal")
        + (dragOver ? " support-composer--dragover" : "")}
      {...composerDragProps}
    >
      {dragOver && (
        <div className="support-composer-drop-hint" aria-hidden>
          ファイルをドロップして添付
        </div>
      )}
      <div className="support-ticket-detail__composer-toolbar">
        {renderModeTabs()}
        <button
          type="button"
          className="support-btn-outline"
          onClick={() => setExpanded(true)}
          title="入力欄を大きく開く"
        >
          ⤢ 全画面で書く
        </button>
        <button
          type="button"
          className="support-btn-outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={sending || pending.length >= MAX_REPLY_ATTACHMENTS}
          title="ファイル添付（画像/動画/PDF/Excel/Word/CSV 等）"
        >
          📎 添付
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT_ATTACHMENTS}
          multiple
          hidden
          onChange={(e) => {
            addPendingFiles(Array.from(e.target.files || []));
            e.target.value = "";
          }}
        />
        <div style={{ position: "relative", marginLeft: "auto" }} ref={aiMenuRef}>
          <button
            type="button"
            className="support-btn-outline"
            onClick={onAiMenuToggle}
            disabled={!!aiBusy}
            title="AI生成。送信前に必ず確認してください"
          >
            {aiBusy ? "AI 処理中…" : "AI ▾"}
          </button>
          {aiMenuOpen && !aiBusy && !expanded && (
            <div className="support-dropdown support-dropdown--align-right support-dropdown--dropup">
              <button type="button" className="support-dropdown__item" onClick={onAiDraft}>AI下書き</button>
              <button type="button" className="support-dropdown__item" onClick={onAiSummarize}>要約</button>
              {faqSuggestEnabled && (
                <button type="button" className="support-dropdown__item" onClick={onAiFaqSuggest}>FAQ化（このチケット）</button>
              )}
              <button type="button" className="support-dropdown__item" onClick={onKbOpen}>過去事例</button>
            </div>
          )}
        </div>
      </div>
      {renderRecipientPicker(true)}
      {aiSummary && (
        <div className="support-ai-summary">
          <button type="button" onClick={onClearAiSummary} title="閉じる" style={{ position: "absolute", top: 4, right: 6, border: "none", background: "transparent", cursor: "pointer", color: "var(--text-secondary,#5f635f)", fontSize: "0.9rem" }}>×</button>
          <div style={{ fontWeight: 700, color: "var(--primary,#358b39)", marginBottom: "0.2rem", fontSize: "0.72rem" }}>AI要約</div>
          {aiSummary}
        </div>
      )}
      {renderPending()}
      <div className={"support-ticket-detail__composer-input support-mention-anchor" + (inputFocused ? " support-ticket-detail__composer-input--focused" : "")}>
        {!expanded && renderMentionSuggest()}
        <textarea
          ref={textareaRef}
          value={reply}
          onChange={(e) => {
            onReplyChange(e.target.value);
            syncMentionState(e.target.value, e.target.selectionStart ?? e.target.value.length);
          }}
          onPaste={handlePaste}
          onClick={(e) => syncMentionState(e.currentTarget.value, e.currentTarget.selectionStart)}
          onKeyUp={(e) => syncMentionState(e.currentTarget.value, e.currentTarget.selectionStart)}
          onKeyDown={onTextareaKeyDown}
          onFocus={() => { setInputFocused(true); if (expanded) expandTextareaRef.current?.focus(); }}
          onBlur={() => setInputFocused(false)}
          placeholder={replyToCustomer ? "顧客へ返信…（⌘/Ctrl+Enter で送信・画像は Ctrl+V / ドラッグで添付）" : "社内メモ（@メンション・📎またはドラッグで添付）…（⌘/Ctrl+Enter で保存）"}
          rows={3}
          readOnly={expanded}
          aria-label={expanded ? "拡大編集中（ポップアップで編集）" : undefined}
        />
        {renderSendBtn()}
      </div>

      {expanded && (
        <div
          className="support-modal-backdrop"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setExpanded(false);
          }}
        >
          <div
            className={"support-modal support-modal--composer" + (dragOver ? " support-composer--dragover" : "")}
            role="dialog"
            aria-modal="true"
            aria-label={replyToCustomer ? "顧客へ返信（拡大）" : "社内メモ（拡大）"}
            onMouseDown={(e) => e.stopPropagation()}
            {...composerDragProps}
          >
            {dragOver && (
              <div className="support-composer-drop-hint support-composer-drop-hint--modal" aria-hidden>
                ファイルをドロップして添付
              </div>
            )}
            <div className="support-modal__header">
              <h3>{replyToCustomer ? "顧客へ返信" : "社内メモ"}（拡大編集）</h3>
              <button type="button" className="support-modal__close" onClick={() => setExpanded(false)} aria-label="閉じる">×</button>
            </div>
            <p className="support-modal__hint">
              Esc で閉じる · ⌘/Ctrl+Enter で送信
              {draftSavedAt
                ? ` · 下書き自動保存 ${new Date(draftSavedAt).toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`
                : ""}
            </p>
            <div className="support-ticket-detail__composer-toolbar" style={{ marginBottom: "0.65rem" }}>
              {renderModeTabs()}
              <button
                type="button"
                className="support-btn-outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending || pending.length >= MAX_REPLY_ATTACHMENTS}
                title="ファイル添付"
              >
                📎 添付
              </button>
              <div style={{ position: "relative", marginLeft: "auto" }}>
                <button
                  type="button"
                  className="support-btn-outline"
                  onClick={onAiMenuToggle}
                  disabled={!!aiBusy}
                  title="AI生成"
                >
                  {aiBusy ? "AI 処理中…" : "AI ▾"}
                </button>
                {aiMenuOpen && !aiBusy && (
                  <div className="support-dropdown support-dropdown--align-right">
                    <button type="button" className="support-dropdown__item" onClick={onAiDraft}>AI下書き</button>
                    <button type="button" className="support-dropdown__item" onClick={onAiSummarize}>要約</button>
                    {faqSuggestEnabled && (
                      <button type="button" className="support-dropdown__item" onClick={onAiFaqSuggest}>FAQ化（このチケット）</button>
                    )}
                    <button type="button" className="support-dropdown__item" onClick={onKbOpen}>過去事例</button>
                  </div>
                )}
              </div>
            </div>
            {renderRecipientPicker(false)}
            {renderPending()}
            <div className="support-composer-expand support-mention-anchor">
              {renderMentionSuggest()}
              <textarea
                ref={expandTextareaRef}
                className="support-composer-expand__textarea"
                value={reply}
                onChange={(e) => {
                  onReplyChange(e.target.value);
                  syncMentionState(e.target.value, e.target.selectionStart ?? e.target.value.length);
                }}
                onPaste={handlePaste}
                onClick={(e) => syncMentionState(e.currentTarget.value, e.currentTarget.selectionStart)}
                onKeyUp={(e) => syncMentionState(e.currentTarget.value, e.currentTarget.selectionStart)}
                onKeyDown={onTextareaKeyDown}
                placeholder={replyToCustomer ? "顧客へ返信…" : "社内メモ…"}
                rows={replyToCustomer ? 12 : 16}
              />
            </div>
            {replyToCustomer && (
              <div className="support-composer-mail-preview">
                <div className="support-composer-mail-preview__label">
                  実際に届くメール（プレビュー）
                  {mailPreview?.subject ? <span className="support-composer-mail-preview__subject"> · {mailPreview.subject}</span> : null}
                </div>
                {mailPreviewErr ? (
                  <div className="support-composer-mail-preview__err">プレビューを取得できませんでした: {mailPreviewErr}</div>
                ) : mailPreview?.suppressed ? (
                  <div className="support-composer-mail-preview__err">{mailPreview.suppressed_text}</div>
                ) : (
                  <div className="support-composer-mail-preview__body">
                    {mailPreview?.salutation && <span className="support-composer-mail-preview__salutation">{mailPreview.salutation}</span>}
                    {mailPreview?.preamble && <span className="support-composer-mail-preview__preamble">{mailPreview.preamble}</span>}
                    <span>{mailPreview?.main ?? (reply.trim() ? "（読み込み中…）" : "本文を入力するとプレビューが表示されます")}</span>
                    {mailPreview?.footer && <span className="support-composer-mail-preview__footer">{mailPreview.footer}</span>}
                  </div>
                )}
              </div>
            )}
            <div className="support-composer-expand__footer">
              <button type="button" className="support-btn-outline" onClick={() => setExpanded(false)}>
                縮小して戻る
              </button>
              {renderSendBtn()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
